import "./CreditBuilder.css";
import ContentPage from "./ContentPage";
import Button from "./Button";
import { ReactComponent as DownArrow } from "./static/icons/DownArrow.svg";
import { ReactComponent as DesignGoals } from "./static/images/DesignGoals.svg";
import CardsCloud from "./CardsCloud";
import { ReactComponent as HypothesisCleo1 } from "./static/images/HypothesisCleo1.svg";
import { ReactComponent as CleoGraph } from "./static/images/CleoGraph.svg";
import Highlighter from "./Highlighter";
import CleoAppVideo from "./static/videos/CleoAppVideo.mp4";
import { ReactComponent as Replay } from "./static/icons/Replay.svg";
import { useEffect, useState } from "react";
import { useRef } from "react";
import AnimatedDiv from "./AnimatedDiv";
import { useInView } from "react-intersection-observer";
import { goToElement } from "./LeftNavButton";

function CreditBuilder() {
  const [videoEnded, setVideoEnded] = useState(false);
  const { ref, inView } = useInView();
  const videoRef = useRef();

  useEffect(() => {
    if (inView && videoRef?.current) {
      videoRef.current.play();
    }
  }, [inView]);

  return (
    <ContentPage>
      <AnimatedDiv>
        <h2>Building credit that doesn't suck</h2>

        <h3>Role</h3>
        <p>UX Researcher | UX Writer | Product Designer </p>

        <h3>First things first. Meet Cleo.</h3>
        <p>
          The last place you'd expect to find a brand that teens and young
          adults love is the world of fintech. Cleo is budget assistant app with
          4 million millennial and Gen Z users. The digital assistant links with
          your bank account to track your spending, manage your budget and
          recommend how much you can afford to save. Cleo has mastered the edgy
          irreverence of Gen Z-ers with flair.
        </p>
        <h3>Project overview</h3>
        <p>
          Goal: Integrate an upsell in our app to increase the number of users
          entering and completing the sign up flow for the credit builder card.
          On top of that I got a business requirement to advertise the other
          subscription plan — Cleo Plus.
        </p>
      </AnimatedDiv>
      <AnimatedDiv>
        <h3>Challenge</h3>
        <p>
          The credit builder card is one of Cleo's premium products. It is a
          part of the subscription service developed to help people with no or
          bad credit score.
        </p>
        <p>
          Quantitative data showed that only 2% of new users signed up for the
          credit builder card within their first week.
        </p>
        <h3>TL;DR</h3>
        <p className="Busy">
          This case is quite long...Busy? Don't worry I got you. Here's the
          summary of the outcome.{" "}
        </p>
        <Button
          className="goToCaseButton"
          variant="complex"
          text="SEE THE FINAL RESULT"
          onClick={() => goToElement("CleoResult")}
          icon={<DownArrow />}
        />
      </AnimatedDiv>
      <AnimatedDiv>
        <h3>Process: Research</h3>
        <p>
          My first step was to investigate the possible cause of such a low
          conversion rate. So, conducting user interviews was just the thing to
          help me. Here's what I found:
        </p>
        <p>
          1. Users noticed and interacted with the credit builder card ads
          hoping to find out more information about the card.
        </p>
        <p>2. The upsell didn't provide users with necessary information.</p>
        <p>
          3. If the users accidentally dismissed the upsell with the sign up
          link, they didn't know how to sign up.
        </p>
      </AnimatedDiv>
      <AnimatedDiv>
        <DesignGoals style={{ margin: "24px 0px" }} />
      </AnimatedDiv>
      <AnimatedDiv>
        <p>
          During the interview I also asked what kind of questions users had
          around the credit builder card. I was interested to understand what
          kind of information they would need in order to make a
          sign-up-for-the-card decision.
        </p>
        <p>
          After analysing the results a clear pattern emerged on what
          information would be valuable to the users. So, my design would've
          needed to address those findings.
        </p>
      </AnimatedDiv>
      <AnimatedDiv>
        <CardsCloud
          cardTexts={[
            "Is this a credit card?",
            "When will I get my physical card?",
            "What is my credit limit?",
            "Where can I use the card and where cannot I?",
            "When should I top up my card and how much?",
            "How much of my credit should I spend?",
          ]}
        ></CardsCloud>
      </AnimatedDiv>
      <p>
        Then I formulated the initial hypothesis and started planning out the
        next steps.
      </p>
      <p>
        * It's important to mention here that when I joined Cleo, the team had a
        siloed approach to working on a project.
      </p>
      <AnimatedDiv>
        <HypothesisCleo1 style={{ margin: "24px 0px" }} />
      </AnimatedDiv>
      <p>
        After the ideation brainstorming session with the team I created the
        first concept of the user flow.{" "}
      </p>
      <p>
        When designing the flow I focused personalised experience — someone who
        doesn't have a credit score at all would see a different version of the
        upsell compared to the one who has a low credit score.
      </p>
      <p>
        However, after the concept review with the team we decided that we would
        like to go with a more generic approach for the first iteration and
        focus on all the user groups.
      </p>
      <h3>Process: Design</h3>
      <p>
        Using the feedback from the team I started working on the visual design.
        The existing design system came in handy. I used the existing components
        as well as created new ones.
      </p>
      <h3>Process: Testing</h3>
      <p>
        After the the design review with internal stakeholders I finalised the
        hifi mockup and prototype that I'd be testing with the users.
      </p>
      <p>
        {" "}
        During the feedback and testing session with the users I focused on the
        design goals and initial problems users mentioned.
      </p>
      <h3 id="CleoResult">Result</h3>
      <p>1. Users understand what the credit builder card is</p>
      <p>2. Users can find answers to their questions</p>
      <p>3. Users understand what they can use the card for</p>
      <p>4. Users can pick a subscription plan</p>
      <p>
        Our primary success metric was the percentage of users entering the card
        sign up funnel. Our secondary success metric was the percentage of users
        subscribing to the card.
      </p>
      <p>
        After 2 weeks the results showed that the new design contributed to a
        significant increase in both of the success metrics, which proved the
        initial hypothesis.
      </p>
      <AnimatedDiv>
        <CleoGraph style={{ margin: "24px 0px" }} />
      </AnimatedDiv>
      <p>The new features and design:</p>
      <p>
        <Highlighter text="1. Interactve chat" />
      </p>
      <p>
        To create personalised experience I added the interactive chat. The
        option users choose here will influence the look of the upsell and the
        plan we promote primarily.
      </p>
      <p>
        <Highlighter text="2. Recommended plan" />
      </p>
      <p>
        Depending on the users' choice in the chat, a plan that fits their needs
        is highlighted. They still have the option to change the plan if they
        want.
      </p>
      <p>
        <Highlighter text="3. Plan comparison" />
      </p>
      <p>
        Plan comparison is not hidden behind a button anymore. Instead,
        different plan options are clearly visible and users can easily switch
        to compare them.
      </p>
      <Highlighter text="4. FAQ section" />
      <p>
        Testing showed high engagement with the FAQ section, so I wanted to keep
        it in the final design to help users find answers to the questions they
        might still have.
      </p>
      <p>
        <Highlighter text="5. Testimonials" />
      </p>
      <p>
        I decided to keep the testimonial section to show users how the credit
        builder card had helped others and build trust. I received lots of
        positive feedback during testing and users enjoyed swiping through them.
      </p>
      <div
        className="VideoAndButton"
        ref={ref}
        style={{ paddingLeft: videoEnded ? "134px" : 0 }}
      >
        <AnimatedDiv>
          <video
            className="CleoAppVideo"
            src={CleoAppVideo}
            width="400"
            height="500"
            muted
            ref={videoRef}
            onEnded={() => setVideoEnded(true)}
          ></video>
        </AnimatedDiv>
        {videoEnded && (
          <Button
            className="ReplayButton"
            variant="complex"
            text="REPLAY"
            onClick={() => {
              videoRef?.current.play();
              setVideoEnded(false);
            }}
            icon={<Replay />}
          />
        )}
      </div>
    </ContentPage>
  );
}

export default CreditBuilder;
